import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { lightDataUpdate, setSceneMeshes } from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import {
  setLoading,
  setLoadingMessage,
} from '../../redux/Slice/globalStates';
import StepNavigation from './components/StepNavigation';
import UploadStep from './components/UploadStep';
import ReviewStep from './components/ReviewStep';
// Team Setup step commented out for now - do not remove
// import TeamSetupStep from './components/TeamSetupStep';
import AssetSelectionStep from './components/AssetSelectionStep';
import StoryboardStep from './components/StoryboardStep';
import { useTrainingState } from './hooks/useTrainingState';
import { updateMomentProperty } from './utils/momentUtils';
import { batchExtractContent } from './utils/documentExtractor';
import { ollamaService } from './utils/ollamaService';
import {
  convertTrainingToVR,
  validateTrainingData,
} from './utils/trainingToVRConverter';
import { assetAutomationService } from './utils/assetAutomationService';
import { AssetLayoutEngine } from './utils/AssetLayoutEngine';
import { generatePlacement, matchPlacement } from './utils/placementService';
import toast from 'react-hot-toast';
import axios from 'axios';
import * as Three from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import './TrainingUpload.css';

export default function TrainingUpload() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const state = useTrainingState();
  const [projectId, setProjectId] = useState(null); // Store projectId in state (will be set when project is created)
  const {
    currentStep,
    setCurrentStep,
    dragActive,
    setDragActive,
    uploadedFiles,
    setUploadedFiles,
    extractedContents,
    setExtractedContents,
    processingFiles,
    setProcessingFiles,
    processingError,
    setProcessingError,
    selectedFile,
    setSelectedFile,
    additionalText,
    setAdditionalText,
    combinedContent,
    setCombinedContent,
    expandedBots,
    setExpandedBots,
    botConfigs,
    setBotConfigs,
    loadedAssets,
    setLoadedAssets,
    assetViewMode,
    setAssetViewMode,
    requiredAssets,
    setRequiredAssets,
    manualEntryText,
    setManualEntryText,
    dragActiveAssets,
    setDragActiveAssets,
    analyzingAssets,
    setAnalyzingAssets,
    analyzedAssets,
    setAnalyzedAssets,
    expandedAssetId,
    setExpandedAssetId,
    searchQuery,
    setSearchQuery,
    selectedMoment,
    setSelectedMoment,
    activeTab,
    setActiveTab,
    isTechnicalMode,
    setIsTechnicalMode,
    chapters,
    setChapters,
    generatingStoryboard,
    setGeneratingStoryboard,
    ollamaConnected,
    setOllamaConnected,
  } = state;

  const fileMapRef = useRef(new Map()); // Map file IDs to actual File objects
  const [gridPageAssets, setGridPageAssets] = useState([]);
  const [loadingGridAssets, setLoadingGridAssets] = useState(false);
  const [loadingTrainingData, setLoadingTrainingData] = useState(false);
  const [savingTrainingData, setSavingTrainingData] = useState(false);
  const [autoFetchingAssets, setAutoFetchingAssets] = useState(false);
  const [fetchProgress, setFetchProgress] = useState(null);
  const [fetchingAssetIds, setFetchingAssetIds] = useState(new Set());
  const [assetPicker, setAssetPicker] = useState({ open: false, asset: null, candidates: [], searching: false, previews: [], selectedPreviewIdx: null, downloadingAll: false });
  const [aiPlacing, setAiPlacing] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Load training data on mount (only if projectId exists - for saved training sessions)
  useEffect(() => {
    if (projectId) {
      loadTrainingData(projectId);
      fetchGridPageAssets(projectId);
    }
  }, [projectId]);

  // Save training data when important fields change (debounced)
  useEffect(() => {
    if (projectId && chapters.length > 0) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Debounce save to avoid too many API calls
      saveTimeoutRef.current = setTimeout(() => {
        saveTrainingData(projectId);
      }, 2000);
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    }
  }, [chapters, projectId]);

  // Save training data when other important fields change
  useEffect(() => {
    if (
      projectId &&
      (botConfigs.length > 0 ||
        loadedAssets.length > 0 ||
        extractedContents.length > 0)
    ) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveTrainingData(projectId);
      }, 2000);
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    }
  }, [
    botConfigs,
    loadedAssets,
    extractedContents,
    analyzedAssets,
    additionalText,
    combinedContent,
    projectId,
  ]);

  // Load training data from backend
  const loadTrainingData = async (id) => {
    try {
      setLoadingTrainingData(true);
      const token = localStorage.getItem('token') || '';
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}experience/training/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        const data = response.data.data;

        // Load chapters if they exist
        if (data.chapters && data.chapters.length > 0) {
          setChapters(data.chapters);
          // Select first moment if available
          if (data.chapters[0]?.moments?.[0]?.id) {
            setSelectedMoment(data.chapters[0].moments[0].id);
          }
        }

        // Load bot configs
        if (data.botConfigs && data.botConfigs.length > 0) {
          setBotConfigs(data.botConfigs);
        }

        // Load assets
        if (data.loadedAssets && data.loadedAssets.length > 0) {
          setLoadedAssets(data.loadedAssets);
        }

        if (data.requiredAssets && data.requiredAssets.length > 0) {
          setRequiredAssets(data.requiredAssets);
        }

        // Load extracted contents
        if (data.extractedContents && data.extractedContents.length > 0) {
          setExtractedContents(data.extractedContents);
        }

        // Load analyzed assets
        if (data.analyzedAssets && data.analyzedAssets.length > 0) {
          setAnalyzedAssets(data.analyzedAssets);
        }

        // Load additional text and combined content
        if (data.additionalText) {
          setAdditionalText(data.additionalText);
        }

        if (data.combinedContent) {
          setCombinedContent(data.combinedContent);
        }

        // If chapters exist, navigate to storyboard step
        if (data.chapters && data.chapters.length > 0) {
          setCurrentStep(5);
          toast.success('Loaded saved training data');
        }
      }
    } catch (error) {
      // 404 is expected for new projects, don't show error
      if (error.response?.status !== 404) {
        console.error('Failed to load training data:', error);
        // Don't show error toast - it's okay if no data exists yet
      }
    } finally {
      setLoadingTrainingData(false);
    }
  };

  // Sanitize data by removing File objects and other non-serializable data
  const sanitizeForAPI = (data) => {
    if (data === null || data === undefined) {
      return data;
    }

    // Handle File objects - exclude them
    if (data instanceof File || data instanceof Blob) {
      return undefined; // Exclude File/Blob objects
    }

    // Handle arrays
    if (Array.isArray(data)) {
      return data
        .map((item) => sanitizeForAPI(item))
        .filter((item) => item !== undefined); // Remove undefined entries
    }

    // Handle objects
    if (typeof data === 'object') {
      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        // Skip File objects and other non-serializable values
        if (value instanceof File || value instanceof Blob) {
          continue; // Skip File/Blob properties
        }
        const sanitizedValue = sanitizeForAPI(value);
        if (sanitizedValue !== undefined) {
          sanitized[key] = sanitizedValue;
        }
      }
      return sanitized;
    }

    // Return primitive values as-is
    return data;
  };

  // Valid asset types for backend
  const VALID_ASSET_TYPES = ['mesh', 'image', 'video', 'audio'];

  // Clean asset data for API - remove invalid fields and File objects
  const cleanAssetForAPI = (asset) => {
    if (!asset || typeof asset !== 'object') {
      return asset;
    }

    const cleaned = { ...asset };

    // Remove File/Blob objects and file references
    delete cleaned.file;
    delete cleaned.originalFile;
    delete cleaned.objPath;
    delete cleaned.reduxObject;

    // Remove invalid enum values - 'fetched' is not valid
    if (cleaned.type === 'fetched') {
      // Remove invalid type, backend will use assetType or default
      delete cleaned.type;
    }

    // Validate and normalize assetType - only allow ['mesh', 'image', 'video', 'audio']
    if (cleaned.assetType && !VALID_ASSET_TYPES.includes(cleaned.assetType)) {
      // If assetType is invalid (e.g., 'light'), remove it or convert to default
      // Try to infer from type field, otherwise default to 'mesh'
      if (cleaned.type && VALID_ASSET_TYPES.includes(cleaned.type)) {
        cleaned.assetType = cleaned.type;
      } else {
        // Remove invalid assetType - backend will use default
        delete cleaned.assetType;
      }
    }

    // Also validate type field
    if (
      cleaned.type &&
      !['gridpage', 'file', 'manual', 'suggested'].includes(cleaned.type)
    ) {
      // If type is invalid, remove it
      delete cleaned.type;
    }

    // Remove source field (not in backend schema enum)
    delete cleaned.source;

    // Remove other non-serializable or non-API fields
    delete cleaned.objectURL;
    delete cleaned.tempId;

    // Recursively clean nested objects to remove any File objects
    return sanitizeForAPI(cleaned);
  };

  // Save training data to backend
  const saveTrainingData = async (id) => {
    if (!id) {
      console.warn('Cannot save training data: no project ID');
      return;
    }

    try {
      setSavingTrainingData(true);
      const token = localStorage.getItem('token') || '';

      // Sanitize payload to remove File objects and invalid enum values
      const payload = sanitizeForAPI({
        chapters,
        botConfigs,
        loadedAssets: loadedAssets.map(cleanAssetForAPI),
        requiredAssets: requiredAssets.map(cleanAssetForAPI),
        analyzedAssets: analyzedAssets.map(cleanAssetForAPI),
        extractedContents: extractedContents.map((ec) => ({
          ...ec,
          // Keep content but ensure no File objects
        })),
        additionalText,
        combinedContent,
      });

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}experience/training/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Silently save - don't show toast on every save
    } catch (error) {
      console.error('Failed to save training data:', error);
      // Don't show error toast for debounced saves to avoid spam
    } finally {
      setSavingTrainingData(false);
    }
  };

  const fetchGridPageAssets = async (id) => {
    if (!id) {
      return; // Don't fetch if no project ID
    }

    try {
      setLoadingGridAssets(true);
      const token = localStorage.getItem('token') || '';
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}assets/project/${id}/assets`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data) {
        const {
          mesh = [],
          image = [],
          video = [],
          audio = [],
        } = response.data.data;

        // Transform GridPage assets to match training asset format
        // IMPORTANT: Preserve temp_file_name for Unity matching (tempId)
        const transformedAssets = [
          ...mesh.map((asset) => {
            const tempId = asset.temp_file_name || asset._id || asset.id;
            return {
              id: asset._id || asset.id,
              tempId: tempId, // Preserve tempId for Unity matching
              name: asset.asset_file_name || asset.file_name || 'Mesh Asset',
              type: 'gridpage',
              assetType: 'mesh',
              url: asset.asset_file_path || asset.url,
              originalAsset: asset, // Keep full asset for temp_file_name access
            };
          }),
          ...image.map((asset) => {
            const tempId = asset.temp_file_name || asset._id || asset.id;
            return {
              id: asset._id || asset.id,
              tempId: tempId,
              name: asset.asset_file_name || asset.file_name || 'Image Asset',
              type: 'gridpage',
              assetType: 'image',
              url: asset.asset_file_path || asset.url,
              originalAsset: asset,
            };
          }),
          ...video.map((asset) => {
            const tempId = asset.temp_file_name || asset._id || asset.id;
            return {
              id: asset._id || asset.id,
              tempId: tempId,
              name: asset.asset_file_name || asset.file_name || 'Video Asset',
              type: 'gridpage',
              assetType: 'video',
              url: asset.asset_file_path || asset.url,
              originalAsset: asset,
            };
          }),
          ...audio.map((asset) => {
            const tempId = asset.temp_file_name || asset._id || asset.id;
            return {
              id: asset._id || asset.id,
              tempId: tempId,
              name: asset.asset_file_name || asset.file_name || 'Audio Asset',
              type: 'gridpage',
              assetType: 'audio',
              url: asset.asset_file_path || asset.url,
              originalAsset: asset,
            };
          }),
        ];

        setGridPageAssets(transformedAssets);
      }
    } catch (error) {
      console.error('Failed to fetch GridPage assets:', error);
      // Don't show error toast - assets might not exist yet
    } finally {
      setLoadingGridAssets(false);
    }
  };

  // Add GridPage asset to loaded assets
  const handleAddGridPageAsset = (asset) => {
    // Check if asset is already in loadedAssets
    if (loadedAssets.some((a) => a.id === asset.id && a.type === 'gridpage')) {
      toast.error('Asset already added');
      return;
    }

    // Get tempId from originalAsset (temp_file_name) or use id
    const tempId = asset.originalAsset?.temp_file_name || asset.id;

    const newAsset = {
      id: asset.id,
      tempId: tempId, // Preserve tempId for Unity matching
      name: asset.name,
      type: 'gridpage',
      assetType: asset.assetType,
      url: asset.url,
      originalAsset: asset.originalAsset,
    };

    setLoadedAssets((prev) => [...prev, newAsset]);
    toast.success(`Added ${asset.name} from GridPage`);
  };

  // Navigate back to Dashboard
  const handleBackToGrid = () => {
    navigate('/dashboard');
  };

  // Test Bedrock connection on mount
  useEffect(() => {
    const testConnection = async () => {
      const result = await ollamaService.testConnection();
      setOllamaConnected(result.connected);
      if (!result.connected) {
        toast.error(
          'Amazon Bedrock is not connected. Please check your AWS credentials and region configuration.'
        );
      }
    };
    testConnection();
  }, [setOllamaConnected]);

  // Combine content when extracted contents or additional text changes
  useEffect(() => {
    const combined = [
      ...extractedContents.map((ec) => ec.content).filter(Boolean),
      additionalText,
    ]
      .filter(Boolean)
      .join('\n\n---\n\n');
    setCombinedContent(combined);
  }, [extractedContents, additionalText, setCombinedContent]);

  // Note: Asset analysis is done manually in Step 4 (Asset Selection), not automatically

  // File upload handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const icons = {
      pdf: '📑',
      doc: '📄',
      docx: '📄',
      txt: '📝',
      md: '📝',
      ppt: '📊',
      pptx: '📊',
      xls: '📊',
      xlsx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      bmp: '🖼️',
      html: '🌐',
      htm: '🌐',
    };
    return icons[ext] || '📄';
  };

  const getFileColor = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const colors = {
      pdf: 'bg-red-600',
      doc: 'bg-blue-600',
      docx: 'bg-blue-600',
      txt: 'bg-gray-600',
      md: 'bg-gray-600',
      ppt: 'bg-orange-600',
      pptx: 'bg-orange-600',
      xls: 'bg-green-600',
      xlsx: 'bg-green-600',
      jpg: 'bg-purple-600',
      jpeg: 'bg-purple-600',
      png: 'bg-purple-600',
      gif: 'bg-purple-600',
      bmp: 'bg-purple-600',
      html: 'bg-yellow-600',
      htm: 'bg-yellow-600',
    };
    return colors[ext] || 'bg-blue-600';
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    const newFiles = fileArray.map((file) => {
      const id = Math.random().toString(36).substring(2, 15);
      fileMapRef.current.set(id, file);
      return {
        name: file.name,
        size: file.size,
        id,
        icon: getFileIcon(file.name),
        color: getFileColor(file.name),
        status: 'pending',
      };
    });

    // Add new files to state immediately
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setProcessingError(null);

    // Extract content from files
    try {
      setProcessingFiles(true);
      const actualFiles = fileArray;
      const result = await batchExtractContent(actualFiles, {
        maxSize: 25 * 1024 * 1024, // 25MB
      });

      // Update file statuses using functional updates to ensure we have the latest state
      setUploadedFiles((prevFiles) => {
        const updatedFiles = [...prevFiles];

        // Update status for successfully extracted files
        for (let index = 0; index < result.results.length; index++) {
          const extracted = result.results[index];
          // Find the file by matching the file name from extraction result
          const fileIndex = updatedFiles.findIndex((f) => {
            // Match by name, or if we have the newFiles array, match by index
            return (
              f.name === extracted.fileName ||
              (index < newFiles.length && f.id === newFiles[index].id)
            );
          });

          if (fileIndex >= 0) {
            updatedFiles[fileIndex] = {
              ...updatedFiles[fileIndex],
              status: 'success',
            };
          }
        }

        // Update status for files with errors
        for (const error of result.errors) {
          const fileIndex = updatedFiles.findIndex(
            (f) => f.name === error.fileName
          );
          if (fileIndex >= 0) {
            updatedFiles[fileIndex] = {
              ...updatedFiles[fileIndex],
              status: 'error',
            };
          }
        }

        return updatedFiles;
      });

      // Store extracted content
      setExtractedContents((prev) => {
        const newExtractedContents = [...prev];
        for (let index = 0; index < result.results.length; index++) {
          const extracted = result.results[index];
          newExtractedContents.push({
            fileId: newFiles[index]?.id,
            fileName: extracted.fileName,
            content: extracted.content,
            metadata: extracted.metadata,
            success: extracted.success,
          });
        }
        return newExtractedContents;
      });

      // Handle errors with toast notifications
      for (const error of result.errors) {
        toast.error(`Failed to extract ${error.fileName}: ${error.error}`);
      }

      if (result.results.length > 0) {
        toast.success(
          `Successfully extracted content from ${result.results.length} file(s)`
        );
      }
    } catch (error) {
      console.error('File extraction error:', error);
      setProcessingError(error.message);
      toast.error(`Failed to extract files: ${error.message}`);

      // Mark files as error on exception
      setUploadedFiles((prevFiles) => {
        return prevFiles.map((file) => {
          const isNewFile = newFiles.some((nf) => nf.id === file.id);
          if (isNewFile && file.status === 'pending') {
            return { ...file, status: 'error' };
          }
          return file;
        });
      });
    } finally {
      setProcessingFiles(false);
    }
  };

  const removeFile = (id) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== id));
    setExtractedContents(extractedContents.filter((ec) => ec.fileId !== id));
    fileMapRef.current.delete(id);
  };

  const clearAllFiles = () => {
    uploadedFiles.forEach((file) => fileMapRef.current.delete(file.id));
    setUploadedFiles([]);
    setExtractedContents([]);
  };

  const handleOpenAssetPicker = async (asset) => {
    // assetType should be the media type (mesh/image/video/audio).
    // asset.type can be 'suggested'/'gridpage'/'file' — not a media type, so ignore those.
    const rawType = asset.assetType || asset.type || 'mesh';
    const assetType = VALID_ASSET_TYPES.includes(rawType) ? rawType : 'mesh';
    if (assetType !== 'mesh') {
      handleAddAnalyzedAsset(asset);
      return;
    }
    const searchOpts = {
      description: asset.description || '',
      usage: asset.usage || '',
      ...(asset.category && { category: asset.category }),
    };

    setAssetPicker({ open: true, asset, candidates: [], searching: true, searchStatus: 'Searching local library…', previews: [], selectedPreviewIdx: null, downloadingAll: false });

    const buildPreviews = (candidates) => candidates.map(c => ({
      uid: c.uid,
      name: c.name,
      author: c.author,
      score: c.relevanceScore,
      tags: c.tags || [],
      thumbnail: c.thumbnail || null,
      source: c.source || 'sketchfab',
      fileUrl: c.fileUrl || null,
      status: 'idle',
      blobUrl: null,
      file: null,
      error: null,
    }));

    try {
      // Step 1 — local library
      const libResult = await assetAutomationService.searchLibraryCandidates(asset.name, searchOpts);
      const libCandidates = libResult.candidates || [];

      if (libCandidates.length > 0) {
        // Show library results immediately while Sketchfab loads in background
        setAssetPicker(prev => ({
          ...prev,
          candidates: libCandidates,
          previews: buildPreviews(libCandidates),
          searchStatus: `Found ${libCandidates.length} in local library. Searching Sketchfab…`,
        }));
      } else {
        setAssetPicker(prev => ({ ...prev, searchStatus: 'Not found in local library. Searching Sketchfab…' }));
      }

      // Step 2 — Sketchfab
      const sfResult = await assetAutomationService.searchSketchfabCandidates(asset.name, searchOpts);
      const sfCandidates = sfResult.candidates || [];

      // Local library results first, then Sketchfab
      const seenUids = new Set();
      const allCandidates = [...libCandidates, ...sfCandidates].filter(c => {
        if (seenUids.has(c.uid)) return false;
        seenUids.add(c.uid);
        return true;
      });

      setAssetPicker(prev => ({
        ...prev,
        candidates: allCandidates,
        previews: buildPreviews(allCandidates),
        searching: false,
        searchStatus: '',
      }));
    } catch (err) {
      setAssetPicker(prev => ({ ...prev, searching: false, searchStatus: '' }));
      toast.error(`Search failed: ${err.message}`);
    }
  };

  // Loads up to 4 models sequentially starting at startIdx.
  // Sequential = at most 1 Sketchfab API call at a time → no 429 rate limit issues.
  // Already-loaded models are skipped so navigating back to a page is instant.
  const handleLoadPage = async (startIdx) => {
    const { candidates, asset, previews } = assetPicker;
    if (!asset || !candidates.length) return;

    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const endIdx = Math.min(startIdx + 4, candidates.length);

    for (let idx = startIdx; idx < endIdx; idx++) {
      const cur = previews[idx];
      if (cur?.status === 'loading' || cur?.status === 'ready') continue;

      if (cur?.blobUrl) URL.revokeObjectURL(cur.blobUrl);
      setAssetPicker(prev => {
        if (!prev.open) return prev;
        const updated = [...prev.previews];
        if (!updated[idx]) return prev;
        updated[idx] = { ...updated[idx], status: 'loading', blobUrl: null, file: null, error: null };
        return { ...prev, previews: updated };
      });

      let lastErr;
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          const result = await assetAutomationService.downloadMeshCandidate(candidates[idx], asset.name);
          if (!result.success) {
            const is429 = result.error?.includes('429');
            if (is429 && attempt < 4) { await sleep(2000 * Math.pow(2, attempt)); continue; }
            throw new Error(result.error || 'Download failed');
          }
          const blobUrl = URL.createObjectURL(result.file);
          setAssetPicker(prev => {
            if (!prev.open) { URL.revokeObjectURL(blobUrl); return prev; }
            const updated = [...prev.previews];
            if (!updated[idx]) return prev;
            updated[idx] = { ...updated[idx], status: 'ready', blobUrl, file: result.file, error: null };
            return { ...prev, previews: updated };
          });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          const is429 = err.message?.includes('429');
          if (is429 && attempt < 4) { await sleep(2000 * Math.pow(2, attempt)); continue; }
          break;
        }
      }

      if (lastErr) {
        setAssetPicker(prev => {
          if (!prev.open) return prev;
          const updated = [...prev.previews];
          if (!updated[idx]) return prev;
          updated[idx] = { ...updated[idx], status: 'error', error: lastErr.message };
          return { ...prev, previews: updated };
        });
      }
    }
  };

  const handleSelectPreview = (idx) => {
    setAssetPicker(prev => ({ ...prev, selectedPreviewIdx: idx }));
  };

  const handleRetryPreview = async (idx) => {
    const { candidates, asset, previews } = assetPicker;
    const candidate = candidates[idx];
    if (!candidate || !asset) return;
    if (previews[idx]?.blobUrl) URL.revokeObjectURL(previews[idx].blobUrl);
    setAssetPicker(prev => {
      if (!prev.open) return prev;
      const updated = [...prev.previews];
      if (!updated[idx]) return prev;
      updated[idx] = { ...updated[idx], status: 'loading', blobUrl: null, file: null, error: null };
      return { ...prev, previews: updated };
    });
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    let lastErr;
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const result = await assetAutomationService.downloadMeshCandidate(candidate, asset.name);
        if (!result.success) {
          const is429 = result.error && result.error.includes('429');
          if (is429 && attempt < 3) { await sleep(2000 * (attempt + 1)); continue; }
          throw new Error(result.error || 'Download failed');
        }
        const blobUrl = URL.createObjectURL(result.file);
        setAssetPicker(prev => {
          if (!prev.open) { URL.revokeObjectURL(blobUrl); return prev; }
          const updated = [...prev.previews];
          if (!updated[idx]) return prev;
          updated[idx] = { ...updated[idx], status: 'ready', blobUrl, file: result.file, error: null };
          return { ...prev, previews: updated };
        });
        return;
      } catch (err) {
        lastErr = err;
        const is429 = err.message && err.message.includes('429');
        if (is429 && attempt < 3) { await sleep(2000 * (attempt + 1)); continue; }
        break;
      }
    }
    setAssetPicker(prev => {
      if (!prev.open) return prev;
      const updated = [...prev.previews];
      if (!updated[idx]) return prev;
      updated[idx] = { ...updated[idx], status: 'error', error: lastErr?.message || 'Download failed' };
      return { ...prev, previews: updated };
    });
  };

  const handleConfirmSelectedPreview = async (overrideIdx) => {
    const { asset, previews, selectedPreviewIdx, candidates } = assetPicker;
    const idx = typeof overrideIdx === 'number' ? overrideIdx : selectedPreviewIdx;
    if (!asset || idx === null || idx === undefined) return;

    const selectedPreview = previews[idx];
    let file = selectedPreview?.file;

    // If not downloaded yet, download now
    if (!file) {
      const candidate = candidates[idx];
      if (!candidate) return;
      setAssetPicker(prev => {
        const updated = [...prev.previews];
        if (updated[idx]) updated[idx] = { ...updated[idx], status: 'loading' };
        return { ...prev, previews: updated };
      });
      const sleep = ms => new Promise(r => setTimeout(r, ms));
      let lastErr;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const result = await assetAutomationService.downloadMeshCandidate(candidate, asset.name);
          if (!result.success) {
            const is429 = result.error && result.error.includes('429');
            if (is429 && attempt < 3) { await sleep(2000 * (attempt + 1)); continue; }
            throw new Error(result.error || 'Download failed');
          }
          file = result.file;
          break;
        } catch (err) {
          lastErr = err;
          const is429 = err.message && err.message.includes('429');
          if (is429 && attempt < 3) { await sleep(2000 * (attempt + 1)); continue; }
          break;
        }
      }
      if (!file) {
        setAssetPicker(prev => {
          const updated = [...prev.previews];
          if (updated[idx]) updated[idx] = { ...updated[idx], status: 'error', error: lastErr?.message || 'Download failed' };
          return { ...prev, previews: updated };
        });
        toast.error(`Download failed: ${lastErr?.message || 'Unknown error'}`);
        return;
      }
    }

    if (selectedPreview?.thumbnail) {
      setAnalyzedAssets(prev => prev.map(a =>
        a.id === asset.id ? { ...a, previewThumbnail: selectedPreview.thumbnail } : a
      ));
    }

    previews.forEach(p => { if (p.blobUrl) URL.revokeObjectURL(p.blobUrl); });
    setAssetPicker({ open: false, asset: null, candidates: [], searching: false, previews: [], selectedPreviewIdx: null, downloadingAll: false });
    setFetchingAssetIds(prev => new Set([...prev, asset.id]));
    toast.loading(`Adding asset…`, { id: `fetch-${asset.id}` });
    try {
      await handleUploadAssetForAnalyzed(asset, file);
      toast.success(`Asset added!`, { id: `fetch-${asset.id}` });
      // Auto-run AI placement after asset is confirmed
      await handleGenerateReferenceLayout();
    } catch (err) {
      toast.error(`Failed: ${err.message}`, { id: `fetch-${asset.id}` });
    } finally {
      setFetchingAssetIds(prev => { const n = new Set(prev); n.delete(asset.id); return n; });
    }
  };

  // Card-level "Let AI Decide": search → pick highest-score → download → add, no modal
  const handleAiAutoFetch = async (asset) => {
    const rawType = asset.assetType || asset.type || 'mesh';
    const assetType = VALID_ASSET_TYPES.includes(rawType) ? rawType : 'mesh';

    if (assetType !== 'mesh') {
      handleAddAnalyzedAsset(asset);
      return;
    }

    setFetchingAssetIds(prev => new Set([...prev, asset.id]));
    toast.loading(`AI is picking the best model for "${asset.name}"…`, { id: `ai-${asset.id}` });

    try {
      const result = await assetAutomationService.searchMeshCandidates(asset.name, {
        description: asset.description || '',
        usage: asset.usage || '',
        ...(asset.category && { category: asset.category }),
      });
      const candidates = result.candidates || [];
      if (!candidates.length) throw new Error('No models found');

      // Prefer local library — already on CloudFront, no external API needed.
      // Fall back to Sketchfab only when the library has no match.
      const libCandidates = candidates.filter(c => c.source === 'library');
      const pool = libCandidates.length > 0 ? libCandidates : candidates;
      const bestIdx = pool.reduce(
        (best, c, i) => ((c.relevanceScore ?? -1) > (pool[best]?.relevanceScore ?? -1) ? i : best),
        0
      );
      const best = pool[bestIdx];

      toast.loading(`Downloading "${best.name}"…`, { id: `ai-${asset.id}` });

      const sleep = ms => new Promise(r => setTimeout(r, ms));
      let file = null;
      let lastErr;
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const dl = await assetAutomationService.downloadMeshCandidate(best, asset.name);
          if (!dl.success) {
            if (dl.error?.includes('429') && attempt < 3) { await sleep(2000 * (attempt + 1)); continue; }
            throw new Error(dl.error || 'Download failed');
          }
          file = dl.file;
          break;
        } catch (err) {
          lastErr = err;
          if (err.message?.includes('429') && attempt < 3) { await sleep(2000 * (attempt + 1)); continue; }
          break;
        }
      }
      if (!file) throw lastErr || new Error('Download failed');

      await handleUploadAssetForAnalyzed(asset, file);
      if (best.thumbnail) {
        setAnalyzedAssets(prev => prev.map(a =>
          a.id === asset.id ? { ...a, previewThumbnail: best.thumbnail } : a
        ));
      }
      toast.success(`AI picked "${best.name}" for ${asset.name}!`, { id: `ai-${asset.id}` });
      // Auto-run AI placement after asset is fetched
      await handleGenerateReferenceLayout();
    } catch (err) {
      toast.error(`AI pick failed: ${err.message}`, { id: `ai-${asset.id}` });
    } finally {
      setFetchingAssetIds(prev => { const n = new Set(prev); n.delete(asset.id); return n; });
    }
  };

  // Bot configuration handlers
  const handleBotConfigChange = (botId, property, value) => {
    setBotConfigs((prev) =>
      prev.map((b) => (b.id === botId ? { ...b, [property]: value } : b))
    );
  };

  const handleBotToggle = (botId) => {
    setExpandedBots((prev) => ({
      ...prev,
      [botId]: !prev[botId],
    }));
  };

  // Asset handlers
  const handleAssetRemove = (assetId) => {
    setLoadedAssets(loadedAssets.filter((a) => a.id !== assetId));
    setRequiredAssets(requiredAssets.filter((a) => a.id !== assetId));
  };

  const handleManualEntryAdd = () => {
    if (manualEntryText.trim()) {
      const newAsset = {
        id: Math.random().toString(36),
        name: manualEntryText.trim(),
        type: 'manual',
      };
      setLoadedAssets((prev) => [...prev, newAsset]);
      setManualEntryText('');
    }
  };

  const handleRequiredAssetAdd = (assetId) => {
    const asset = loadedAssets.find((a) => a.id === assetId);
    if (asset && !requiredAssets.some((a) => a.id === assetId)) {
      setRequiredAssets([...requiredAssets, asset]);
    }
  };

  const handleRequiredAssetRemove = (assetId) => {
    setRequiredAssets(requiredAssets.filter((a) => a.id !== assetId));
  };

  const handleDragAssets = (isActive) => {
    setDragActiveAssets(isActive);
  };

  // Update asset information (description, usage, etc.)
  const handleAssetInfoUpdate = (assetId, field, value) => {
    setAnalyzedAssets((prev) =>
      prev.map((asset) =>
        asset.id === assetId ? { ...asset, [field]: value } : asset
      )
    );
    // Also update in loadedAssets if it exists there
    setLoadedAssets((prev) =>
      prev.map((asset) =>
        asset.id === assetId ? { ...asset, [field]: value } : asset
      )
    );
  };

  // Upload asset file for an analyzed asset (allows replacing existing assets)
  const handleUploadAssetForAnalyzed = async (asset, file) => {
    try {
      // Determine asset type from file extension
      const fileName = file.name.toLowerCase();
      let assetType = 'mesh';
      if (fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
        assetType = 'image';
      } else if (fileName.match(/\.(mp4|avi|mov|webm)$/)) {
        assetType = 'video';
      } else if (fileName.match(/\.(mp3|wav|ogg|m4a)$/)) {
        assetType = 'audio';
      } else if (fileName.match(/\.(glb|gltf|fbx|obj|dae|3ds|blend)$/)) {
        assetType = 'mesh';
      }

      // Validate assetType - only allow valid types
      if (!VALID_ASSET_TYPES.includes(assetType)) {
        assetType = 'mesh'; // Default to mesh if invalid
      }

      // Get transform values from asset (LLM-generated or default)
      let transform = asset.transform || {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
      };

      // SMART RESIZING LOGIC for uploaded mesh assets
      if (assetType === 'mesh' && file) {
        try {
          toast.loading(`Calculating optimal size for ${asset.name}...`, {
            id: `upload-size-${asset.id}`,
          });

          // Get expected size from LLM
          const expectedSize = await ollamaService.getExpectedSize(
            asset.name,
            asset.description
          );

          if (expectedSize) {
            // Load the model to measure it
            const objectURL = URL.createObjectURL(file);
            const loader = new GLTFLoader();
            const dracoLoader = new DRACOLoader();
            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
            loader.setDRACOLoader(dracoLoader);

            await new Promise((resolve) => {
              loader.load(
                objectURL,
                (gltf) => {
                  const model = gltf.scene;
                  const size = getObjectSizeInCM(model, Three);

                  if (size) {
                    // Calculate max dimension of current model
                    const maxDim = Math.max(size.width, size.height, size.depth);

                    // Calculate scale factor
                    if (maxDim > 0) {
                      const scaleFactor = expectedSize / maxDim;

                      console.log(`Smart Resizing (Upload): ${asset.name}`);
                      console.log(`Expected size: ${expectedSize} cm`);
                      console.log(`Current size: ${maxDim} cm`);
                      console.log(`Scale factor: ${scaleFactor}`);

                      // Apply scale factor to existing scale
                      transform = {
                        ...transform,
                        scale: {
                          x: (transform.scale?.x || 1) * scaleFactor,
                          y: (transform.scale?.y || 1) * scaleFactor,
                          z: (transform.scale?.z || 1) * scaleFactor,
                        }
                      };
                    }
                  }
                  URL.revokeObjectURL(objectURL);
                  resolve();
                },
                undefined,
                (err) => {
                  console.error('Failed to measure uploaded asset for resizing', err);
                  URL.revokeObjectURL(objectURL);
                  resolve(); // Continue even if measurement fails
                }
              );
            });

            toast.success(`Analyzed and resized ${asset.name}!`, {
              id: `upload-size-${asset.id}`,
            });
          } else {
            toast.dismiss(`upload-size-${asset.id}`);
          }
        } catch (resizeError) {
          console.error('Smart resizing failed for upload:', resizeError);
          toast.dismiss(`upload-size-${asset.id}`);
          // Continue without resizing
        }
      }

      // Convert rotation from degrees to radians for Three.js compatibility
      const rotationInRadians = {
        x: (transform.rotation?.x || 0) * (Math.PI / 180),
        y: (transform.rotation?.y || 0) * (Math.PI / 180),
        z: (transform.rotation?.z || 0) * (Math.PI / 180),
      };

      // Add asset to Redux state (same as fetched assets)
      const reduxResult = assetAutomationService.addAssetToReduxState(
        file,
        assetType,
        asset.name,
        {
          description: asset.description || '',
          usage: asset.usage || '',
          // Pass LLM-generated transform values with smart scaling applied
          position: transform.position || { x: 0, y: 0, z: 0 },
          rotation: rotationInRadians, // Converted to radians
          scale: transform.scale || { x: 1, y: 1, z: 1 },
        }
      );

      if (!reduxResult.success) {
        throw new Error('Failed to add asset to Redux state');
      }

      // Create asset entry with Redux state information (same structure as fetched assets)
      const uploadedAsset = {
        id: reduxResult.tempId, // Use tempId as the ID (matches fetched assets)
        name: asset.name,
        type: 'file',
        assetType: assetType,
        url: reduxResult.objectURL,
        tempId: reduxResult.tempId, // Critical for Unity matching
        file: file, // Store file reference (will be removed before API call)
        description: asset.description || '',
        usage: asset.usage || '',
        mandatory: asset.mandatory || false,
        status: 'available',
        // Preserve LLM-generated transform values with smart scaling
        transform: transform,
        // Store Redux object for reference (will be removed before API call)
        reduxObject: reduxResult.assetObject,
        // Store original analyzed asset ID for reference
        originalAnalyzedAssetId: asset.id,
      };

      // Check if asset already exists - replace it instead of adding duplicate
      // Match by analyzed asset ID or by originalAnalyzedAssetId (for fetched assets that were replaced)
      const existingIndex = loadedAssets.findIndex((a) => {
        // Match by analyzed asset ID
        if (a.id === asset.id || a.originalAnalyzedAssetId === asset.id) {
          return true;
        }
        // Also check if analyzedAssets was updated with this tempId
        const analyzedAsset = analyzedAssets.find((aa) => aa.id === asset.id);
        if (analyzedAsset && (a.id === analyzedAsset.tempId || a.tempId === analyzedAsset.tempId)) {
          return true;
        }
        return false;
      });

      if (existingIndex >= 0) {
        // Replace existing asset
        setLoadedAssets((prev) => {
          const updated = [...prev];
          updated[existingIndex] = uploadedAsset;
          return updated;
        });
        toast.success(`Replaced ${asset.name} with uploaded file`);
      } else {
        // Add new asset
        setLoadedAssets((prev) => [...prev, uploadedAsset]);
        toast.success(`Uploaded ${asset.name} successfully!`);
      }

      // Update analyzedAssets to mark as available
      setAnalyzedAssets((prev) =>
        prev.map((a) =>
          a.id === asset.id
            ? {
              ...a,
              status: 'available',
              id: reduxResult.tempId,
              type: 'file',
              assetType: assetType,
              url: reduxResult.objectURL,
              tempId: reduxResult.tempId,
              transform: transform, // Include updated transform
            }
            : a
        )
      );

      // Update required assets if it exists there
      // Match by analyzed asset ID, originalAnalyzedAssetId, or tempId
      const requiredIndex = requiredAssets.findIndex((a) => {
        if (a.id === asset.id || a.originalAnalyzedAssetId === asset.id) {
          return true;
        }
        // Check if this asset was previously added with a different ID
        if (a.id === reduxResult.tempId || a.tempId === reduxResult.tempId) {
          return true;
        }
        return false;
      });

      if (requiredIndex >= 0) {
        setRequiredAssets((prev) => {
          const updated = [...prev];
          updated[requiredIndex] = uploadedAsset;
          return updated;
        });
      } else if (asset.mandatory) {
        // If mandatory and not in required, add it
        setRequiredAssets((prev) => {
          if (prev.some((a) =>
            a.id === reduxResult.tempId ||
            a.tempId === reduxResult.tempId ||
            a.originalAnalyzedAssetId === asset.id
          )) {
            return prev;
          }
          return [...prev, uploadedAsset];
        });
      }
    } catch (error) {
      console.error('Error uploading asset:', error);
      toast.error(`Failed to upload ${asset.name}: ${error.message}`);
    }
  };

  function getObjectSizeInCM(object, Three) {
    object.updateWorldMatrix(true, true);

    const box = new Three.Box3().setFromObject(object);

    if (box.isEmpty()) {
      return null;
    }

    const size = new Three.Vector3();
    box.getSize(size);

    return {
      width: size.x * 100,   // meters → cm
      height: size.y * 100,
      depth: size.z * 100,
    };
  }

  // Add analyzed asset to loaded assets - with auto-fetch and smart resizing
  const handleAddAnalyzedAsset = async (asset) => {
    const originalAnalyzedAssetId = asset?.originalAnalyzedAssetId || asset?.id;

    // Check if already in loadedAssets (dedupe even if analyzed asset id changes after fetch/upload)
    if (
      loadedAssets.some(
        (a) =>
          a.id === asset.id ||
          a.id === originalAnalyzedAssetId ||
          a.originalAnalyzedAssetId === originalAnalyzedAssetId
      )
    ) {
      toast.error('Asset already added');
      return;
    }

    // If asset is "needed" or not available, fetch it from internet
    if (asset.status === 'needed' || !asset.status || !asset.url) {
      try {
        setFetchingAssetIds((prev) => new Set([...prev, asset.id]));
        toast.loading(`Fetching ${asset.name} from the internet...`, {
          id: `fetch-${asset.id}`,
        });

        // Validate assetType - only allow valid types
        let assetType = asset.assetType || asset.type || 'mesh';
        if (!VALID_ASSET_TYPES.includes(assetType)) {
          assetType = 'mesh'; // Default to mesh if invalid
        }

        // 1. Get expected size from LLM (in parallel with fetch for speed)
        // Only for meshes, as images/videos don't need real-world scaling in the same way
        let expectedSizePromise = Promise.resolve(null);
        if (assetType === 'mesh') {
          toast.loading(`Calculating optimal size for ${asset.name}...`, {
            id: `fetch-${asset.id}`,
          });
          expectedSizePromise = ollamaService.getExpectedSize(
            asset.name,
            asset.description
          );
        }

        let fetchResult;

        // Fetch based on asset type
        switch (assetType) {
          case 'mesh':
            fetchResult = await assetAutomationService.fetchMeshAsset(
              asset.name,
              {
                description: asset.description || '',
                usage: asset.usage || '',
                ...(asset.category && { category: asset.category }),
              }
            );
            break;
          case 'image':
            fetchResult = await assetAutomationService.fetchImageAsset(
              asset.name,
              {
                description: asset.description || '',
                usage: asset.usage || '',
              }
            );
            break;
          case 'video':
            fetchResult = await assetAutomationService.fetchVideoAsset(
              asset.name,
              {
                description: asset.description || '',
                usage: asset.usage || '',
              }
            );
            break;
          case 'audio':
            fetchResult = await assetAutomationService.fetchAudioAsset(
              asset.name,
              {
                description: asset.description || '',
                usage: asset.usage || '',
              }
            );
            break;
          default:
            throw new Error(`Unsupported asset type: ${assetType}`);
        }

        if (!fetchResult.success) {
          throw new Error(fetchResult.error || 'Failed to fetch asset');
        }

        // Validate assetType before adding to Redux
        const validAssetType = VALID_ASSET_TYPES.includes(fetchResult.assetType)
          ? fetchResult.assetType
          : 'mesh'; // Default to mesh if invalid

        console.log(asset, '--->asset');

        // Use defaults or LLM-generated transform values
        let transform = asset.transform || {
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
          scale: { x: 1, y: 1, z: 1 },
        };

        // 2. SMART RESIZING LOGIC
        if (assetType === 'mesh' && fetchResult.file) {
          try {
            const expectedSize = await expectedSizePromise;

            if (expectedSize) {
              // Load the model to measure it
              const objectURL = URL.createObjectURL(fetchResult.file);
              const loader = new GLTFLoader();
              const dracoLoader = new DRACOLoader();
              dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
              loader.setDRACOLoader(dracoLoader);

              await new Promise((resolve) => {
                loader.load(
                  objectURL,
                  (gltf) => {
                    const model = gltf.scene;
                    const size = getObjectSizeInCM(model, Three);

                    if (size) {
                      // Calculate max dimension of current model
                      const maxDim = Math.max(size.width, size.height, size.depth);

                      // Calculate scale factor
                      // If expected is 100cm and current is 50cm, scale should be 2
                      if (maxDim > 0) {
                        const scaleFactor = expectedSize / maxDim;

                        console.log(`Smart Resizing: ${asset.name}`);
                        console.log(`Expected size: ${expectedSize} cm`);
                        console.log(`Current size: ${maxDim} cm`);
                        console.log(`Scale factor: ${scaleFactor}`);

                        // Apply scale factor to existing scale
                        transform = {
                          ...transform,
                          scale: {
                            x: (transform.scale?.x || 1) * scaleFactor,
                            y: (transform.scale?.y || 1) * scaleFactor,
                            z: (transform.scale?.z || 1) * scaleFactor,
                          }
                        };
                      }
                    }
                    URL.revokeObjectURL(objectURL);
                    resolve();
                  },
                  undefined,
                  (err) => {
                    console.error('Failed to measure asset for resizing', err);
                    URL.revokeObjectURL(objectURL);
                    resolve(); // Continue even if measurement fails
                  }
                );
              });
            }
          } catch (resizeError) {
            console.error('Smart resizing failed:', resizeError);
            // Continue without resizing
          }
        }

        // Convert rotation from degrees to radians for Three.js compatibility
        const rotationInRadians = {
          x: (transform.rotation?.x || 0) * (Math.PI / 180),
          y: (transform.rotation?.y || 180) * (Math.PI / 180), // Default 180 flip often needed for imported models
          z: (transform.rotation?.z || 0) * (Math.PI / 180),
        };

        const reduxResult = assetAutomationService.addAssetToReduxState(
          fetchResult.file,
          validAssetType,
          asset.name,
          {
            ...fetchResult.metadata,
            description: asset.description || '',
            usage: asset.usage || '',
            source: fetchResult.source,
            // Pass the calculated transform values
            position: transform.position || { x: 0, y: 0, z: 0 },
            rotation: rotationInRadians,
            scale: transform.scale || { x: 1, y: 1, z: 1 },
          }
        );

        if (!reduxResult.success) {
          throw new Error('Failed to add asset to state');
        }

        // Update asset with fetched information
        const fetchedAsset = {
          id: reduxResult.tempId,
          originalAnalyzedAssetId,
          name: asset.name,
          type: 'file',
          assetType: validAssetType,
          url: reduxResult.objectURL,
          tempId: reduxResult.tempId,
          file: fetchResult.file,
          description: asset.description || '',
          usage: asset.usage || '',
          mandatory: asset.mandatory || false,
          status: 'available',
          transform: transform,
          reduxObject: reduxResult.assetObject,
        };

        // Add to loadedAssets
        setLoadedAssets((prev) => [...prev, fetchedAsset]);

        // Update analyzedAssets to mark as available
        setAnalyzedAssets((prev) =>
          prev.map((a) =>
            a.id === asset.id || a.id === originalAnalyzedAssetId
              ? {
                ...a,
                status: 'available',
                id: reduxResult.tempId,
                originalAnalyzedAssetId,
                type: 'file',
                assetType: validAssetType,
                url: reduxResult.objectURL,
                tempId: reduxResult.tempId,
                transform: transform, // Update transform in analyzed asset too
              }
              : a
          )
        );

        // If mandatory, auto-add to required assets
        if (asset.mandatory) {
          setRequiredAssets((prev) => {
            if (prev.some((a) => a.id === reduxResult.tempId)) {
              return prev;
            }
            return [...prev, fetchedAsset];
          });
        }

        toast.success(`Successfully fetched and resized ${asset.name}!`, {
          id: `fetch-${asset.id}`,
        });
        setFetchingAssetIds((prev) => { const next = new Set(prev); next.delete(asset.id); return next; });
      } catch (error) {
        console.error('Error fetching asset:', error);
        toast.error(`Failed to fetch ${asset.name}: ${error.message}`, {
          id: `fetch-${asset.id}`,
        });
        setFetchingAssetIds((prev) => { const next = new Set(prev); next.delete(asset.id); return next; });

        // If fetch fails, still add it as a "needed" asset so user can manually add it later
        const newAsset = {
          id: asset.id,
          name: asset.name,
          type: asset.type || 'file',
          assetType: asset.assetType || asset.type || 'mesh',
          description: asset.description || '',
          usage: asset.usage || '',
          mandatory: asset.mandatory || false,
          status: 'needed',
        };
        setLoadedAssets((prev) => [...prev, newAsset]);
      }
    } else {
      // Asset is already available, analyze and resize it
      console.log('Asset is already available:', asset);
      try {
        let transform = asset.transform || {
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
          scale: { x: 1, y: 1, z: 1 },
        };

        const assetType = asset.assetType || asset.type || 'mesh';

        // SMART RESIZING LOGIC for existing assets
        if (assetType === 'mesh' && asset.url) {
          try {
            toast.loading(`Calculating optimal size for ${asset.name}...`, {
              id: `size-${asset.id}`,
            });

            const expectedSize = await ollamaService.getExpectedSize(
              asset.name,
              asset.description
            );

            if (expectedSize) {
              // Load the model to measure it
              const loader = new GLTFLoader();
              const dracoLoader = new DRACOLoader();
              dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
              loader.setDRACOLoader(dracoLoader);

              await new Promise((resolve) => {
                loader.load(
                  asset.url,
                  (gltf) => {
                    const model = gltf.scene;
                    const size = getObjectSizeInCM(model, Three);

                    if (size) {
                      // Calculate max dimension of current model
                      const maxDim = Math.max(size.width, size.height, size.depth);

                      // Calculate scale factor
                      if (maxDim > 0) {
                        const scaleFactor = expectedSize / maxDim;

                        console.log(`Smart Resizing: ${asset.name}`);
                        console.log(`Expected size: ${expectedSize} cm`);
                        console.log(`Current size: ${maxDim} cm`);
                        console.log(`Scale factor: ${scaleFactor}`);

                        // Apply scale factor to existing scale
                        transform = {
                          ...transform,
                          scale: {
                            x: (transform.scale?.x || 1) * scaleFactor,
                            y: (transform.scale?.y || 1) * scaleFactor,
                            z: (transform.scale?.z || 1) * scaleFactor,
                          }
                        };
                      }
                    }
                    resolve();
                  },
                  undefined,
                  (err) => {
                    console.error('Failed to measure asset for resizing', err);
                    resolve(); // Continue even if measurement fails
                  }
                );
              });

              toast.success(`Analyzed and resized ${asset.name}!`, {
                id: `size-${asset.id}`,
              });
            } else {
              toast.dismiss(`size-${asset.id}`);
            }
          } catch (resizeError) {
            console.error('Smart resizing failed:', resizeError);
            toast.dismiss(`size-${asset.id}`);
            // Continue without resizing
          }
        }

        // Add the asset with the calculated transform
        const newAsset = {
          id: asset.id,
          name: asset.name,
          type: asset.type || 'file',
          assetType: assetType,
          description: asset.description || '',
          usage: asset.usage || '',
          mandatory: asset.mandatory || false,
          status: asset.status || 'available',
          url: asset.url,
          transform: transform, // Include the updated transform
        };

        setLoadedAssets((prev) => [...prev, newAsset]);
        toast.success(`Added ${asset.name}`);

      } catch (error) {
        console.error('Error processing existing asset:', error);

        // Fallback: add asset without resizing
        const newAsset = {
          id: asset.id,
          name: asset.name,
          type: asset.type || 'file',
          assetType: asset.assetType || asset.type || 'mesh',
          description: asset.description || '',
          usage: asset.usage || '',
          mandatory: asset.mandatory || false,
          status: asset.status || 'available',
          url: asset.url,
        };

        setLoadedAssets((prev) => [...prev, newAsset]);
        toast.success(`Added ${asset.name}`);
      }
    }
  };

  const handleDropAssets = (e) => {
    // Check if files were dropped
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newAssets = Array.from(e.dataTransfer.files).map((file) => ({
        id: Math.random().toString(36),
        name: file.name,
        type: 'file',
        file: file,
      }));
      setLoadedAssets((prev) => [...prev, ...newAssets]);
    }
    // Check if asset ID was dragged from loaded assets
    else {
      const assetId = e.dataTransfer.getData('assetId');
      if (assetId) {
        handleRequiredAssetAdd(assetId);
      }
    }
  };

  // Moment update handler
  const handleMomentUpdate = (momentId, propertyPath, value) => {
    const updatedChapters = updateMomentProperty(
      chapters,
      momentId,
      propertyPath,
      value
    );
    setChapters(updatedChapters);
  };

  // Generate storyboard using LLM
  const handleGenerateStoryboard = async () => {
    if (!combinedContent.trim()) {
      toast.error('Please upload files or add text content first');
      return false;
    }

    if (!ollamaConnected) {
      toast.error('Ollama is not connected. Please ensure Ollama is running.');
      return false;
    }

    try {
      setGeneratingStoryboard(true);
      toast.loading('Generating storyboard...', { id: 'storyboard' });

      // Get researcher bot config
      const researcherBot = botConfigs.find((b) => b.id === 'researcher');
      const botDescription = researcherBot?.description || '';

      // Validate that we have assets before generating storyboard
      if (!analyzedAssets || analyzedAssets.length === 0) {
        toast.error('Please analyze and select assets first in Step 4', {
          id: 'storyboard',
        });
        return false;
      }

      // Skip unfetched mandatory assets — only available assets are used for generation
      const mandatoryNeeded = analyzedAssets.filter(
        (asset) => asset.mandatory && asset.status === 'needed'
      );
      if (mandatoryNeeded.length > 0) {
        toast(`${mandatoryNeeded.length} unfetched asset(s) will be skipped`, { duration: 3000 });
      }

      // Get content from extracted documents only (not additionalText)
      const documentContent = extractedContents
        .filter(
          (ec) => ec.success && ec.content && ec.content.trim().length > 0
        )
        .map((ec) => ec.content)
        .join('\n\n---\n\n');

      if (!documentContent || documentContent.trim().length < 100) {
        toast.error(
          'Document content is too short. Please upload documents with sufficient content.',
          {
            id: 'storyboard',
          }
        );
        return false;
      }

      // Generate chapters using Ollama - ASSET-DRIVEN
      // Pass only AVAILABLE required assets so chapters are built from uploaded assets only
      const availableAssets = requiredAssets.filter((a) => a.status === 'available');
      const assetsForGeneration = availableAssets.length > 0 ? availableAssets : requiredAssets;
      const generatedChapters = await ollamaService.generateChapters(
        documentContent,
        botDescription,
        assetsForGeneration
      );

      if (generatedChapters.length > 0) {
        setChapters(generatedChapters);
        if (generatedChapters[0]?.moments?.[0]?.id) {
          setSelectedMoment(generatedChapters[0].moments[0].id);
        }

        // Save immediately after generation (if project exists)
        if (projectId) {
          await saveTrainingData(projectId);
        }

        toast.success(
          `Generated ${generatedChapters.length} chapter(s) with ${generatedChapters.reduce(
            (sum, ch) => sum + (ch.moments?.length || 0),
            0
          )} moment(s)`,
          { id: 'storyboard' }
        );
        return true;
      }

      toast.error('Failed to generate storyboard. Please try again.', {
        id: 'storyboard',
      });
      return false;
    } catch (error) {
      console.error('Storyboard generation error:', error);
      toast.error(`Failed to generate storyboard: ${error.message}`, {
        id: 'storyboard',
      });
      return false;
    } finally {
      setGeneratingStoryboard(false);
    }
  };

  // Note: Asset analysis is now done manually in Step 4 via handleReanalyzeAssets
  // Removed auto-analysis function - analysis only happens when user clicks "Analyze Documents" in Step 4

  // Automatically fetch needed assets from the internet
  const handleAutoFetchAssets = async () => {
    // Get all assets that are needed
    const neededAssets = analyzedAssets.filter(
      (asset) => asset.status === 'needed' || !asset.status
    );

    if (neededAssets.length === 0) {
      toast.error('No assets need to be fetched. All assets are available.');
      return;
    }

    // Note: Auto-fetch requires a project to upload to
    // For now, we'll show a message that project will be created on publish
    if (!projectId) {
      toast(
        'Assets will be uploaded when you publish. Project will be created at that time.',
        { duration: 5000 }
      );
      return;
    }

    // Confirm with user
    const confirmMessage = `This will automatically fetch ${neededAssets.length} asset(s) from the internet:\n\n${neededAssets.map((a) => `- ${a.name} (${a.assetType || a.type || 'mesh'})`).join('\n')}\n\nContinue?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setAutoFetchingAssets(true);
      setFetchProgress({
        current: 0,
        total: neededAssets.length,
        asset: '',
        status: 'starting',
      });

      const result = await assetAutomationService.autoFetchAssets(
        projectId,
        neededAssets,
        {
          onProgress: (progress) => {
            setFetchProgress(progress);
          },
          onAssetFetched: async (fetchedAsset) => {
            // Update the asset in analyzedAssets to mark as available
            setAnalyzedAssets((prev) =>
              prev.map((asset) =>
                asset.id === fetchedAsset.originalAsset.id
                  ? {
                    ...asset,
                    status: 'available',
                    id: fetchedAsset.assetId,
                    type: 'gridpage', // Valid enum value
                    url:
                      fetchedAsset.uploadedAsset?.asset_file_path ||
                      fetchedAsset.uploadedAsset?.url,
                    // Preserve LLM-generated transform values
                    transform: asset.transform || {
                      position: { x: 0, y: 0, z: 0 },
                      rotation: { x: 0, y: 0, z: 0 },
                      scale: { x: 1, y: 1, z: 1 },
                    },
                    // Remove source field - not in backend schema
                  }
                  : asset
              )
            );

            // Add to loadedAssets if not already there
            setLoadedAssets((prev) => {
              if (prev.some((a) => a.id === fetchedAsset.assetId)) {
                return prev;
              }
              return [
                ...prev,
                {
                  id: fetchedAsset.assetId,
                  name: fetchedAsset.originalAsset.name,
                  type: 'gridpage',
                  assetType:
                    fetchedAsset.originalAsset.assetType ||
                    fetchedAsset.originalAsset.type ||
                    'mesh',
                  url:
                    fetchedAsset.uploadedAsset?.asset_file_path ||
                    fetchedAsset.uploadedAsset?.url,
                  originalAsset: fetchedAsset.uploadedAsset,
                  // Preserve LLM-generated transform values
                  transform: fetchedAsset.originalAsset.transform || {
                    position: { x: 0, y: 0, z: 0 },
                    rotation: { x: 0, y: 0, z: 0 },
                    scale: { x: 1, y: 1, z: 1 },
                  },
                },
              ];
            });

            // If mandatory, auto-add to required assets
            if (fetchedAsset.originalAsset.mandatory) {
              setRequiredAssets((prev) => {
                if (prev.some((a) => a.id === fetchedAsset.assetId)) {
                  return prev;
                }
                return [
                  ...prev,
                  {
                    id: fetchedAsset.assetId,
                    name: fetchedAsset.originalAsset.name,
                    type: 'gridpage',
                    assetType:
                      fetchedAsset.originalAsset.assetType ||
                      fetchedAsset.originalAsset.type ||
                      'mesh',
                    url:
                      fetchedAsset.uploadedAsset?.asset_file_path ||
                      fetchedAsset.uploadedAsset?.url,
                    // Preserve LLM-generated transform values
                    transform: fetchedAsset.originalAsset.transform || {
                      position: { x: 0, y: 0, z: 0 },
                      rotation: { x: 0, y: 0, z: 0 },
                      scale: { x: 1, y: 1, z: 1 },
                    },
                  },
                ];
              });
            }

            // Refresh GridPage assets to include newly uploaded ones (if project exists)
            if (projectId) {
              await fetchGridPageAssets(projectId);
            }
          },
        }
      );

      if (result.success) {
        toast.success(
          `Successfully fetched ${result.summary.success} asset(s)! Refreshing assets...`
        );
        // Refresh GridPage assets (if project exists)
        if (projectId) {
          await fetchGridPageAssets(projectId);
        }
        // Auto-run AI placement after all assets are fetched
        await handleGenerateReferenceLayout();
      }
    } catch (error) {
      console.error('Auto-fetch assets error:', error);
      toast.error(`Failed to fetch assets: ${error.message}`);
    } finally {
      setAutoFetchingAssets(false);
      setFetchProgress(null);
    }
  };

  const handleGenerateReferenceLayout = async () => {
    const availableAssets = analyzedAssets.filter(a => a.status === 'available');
    if (availableAssets.length === 0) {
      toast.error('Fetch or upload assets first before using AI placement');
      return;
    }
    if (!combinedContent || combinedContent.trim().length < 50) {
      toast.error('SOP content not available. Please upload and analyze documents first.');
      return;
    }

    if (aiPlacing) return; // prevent concurrent runs
    setAiPlacing(true);
    toast.loading('AI placing assets...', { id: 'ai-place' });

    try {
      const placements = await generatePlacement(combinedContent, availableAssets);

      // Enforce correct Y heights and clamp X,Z to building footprint
      const HEIGHT_BY_SURFACE = { rooftop: 3.5, rooftop_wall: 3.8, wall: 1.5, table: 0.75, floor: 0 };
      const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
      const correctedPlacements = placements.map(p => {
        const corrected = { ...p, y: HEIGHT_BY_SURFACE[p.surface] ?? (p.y || 0) };
        if (p.surface === 'rooftop' || p.surface === 'rooftop_wall') {
          corrected.x = clamp(p.x, -3, 3);
          corrected.z = clamp(p.z, -3, 3);
        }
        return corrected;
      });

      const matchResults = availableAssets.map((asset, availableIdx) => ({
        asset,
        placement: matchPlacement(asset.name, correctedPlacements, availableIdx)
      }));
      const matched = matchResults.filter(r => r.placement).length;

      setAnalyzedAssets(prev => prev.map((asset) => {
        const availableIdx = availableAssets.findIndex(a => a.id === asset.id);
        if (availableIdx === -1) return asset;
        const placement = matchResults[availableIdx]?.placement;
        if (!placement) return asset;
        return {
          ...asset,
          transform: {
            position: { x: placement.x, y: placement.y, z: placement.z },
            rotation: { x: placement.rotationX || 0, y: placement.rotationY || 0, z: placement.rotationZ || 0 },
            scale: asset.transform?.scale || { x: 1, y: 1, z: 1 }
          },
          placementSource: 'bedrock',
          placementSurface: placement.surface,
          placementConfidence: placement.confidence,
          placementDimensions: placement.width_cm
            ? { width_cm: placement.width_cm, height_cm: placement.height_cm, depth_cm: placement.depth_cm }
            : undefined,
        };
      }));

      toast.success(`AI placed ${matched}/${availableAssets.length} assets!`, { id: 'ai-place' });
    } catch (err) {
      console.error('AI placement error:', err);
      toast.error(`AI placement failed: ${err.message}`, { id: 'ai-place' });
    } finally {
      setAiPlacing(false);
    }
  };

  // Re-analyze assets - only based on extracted documents from Step 1
  const handleReanalyzeAssets = async () => {
    // Check if we have actual extracted document content (not just additionalText)
    const hasExtractedDocuments = extractedContents.some(
      (ec) => ec.success && ec.content && ec.content.trim().length > 0
    );

    if (!hasExtractedDocuments) {
      toast.error('Please upload and extract documents in Step 1 first');
      return;
    }

    // Get content from extracted documents only (exclude additionalText)
    const documentContent = extractedContents
      .filter((ec) => ec.success && ec.content && ec.content.trim().length > 0)
      .map((ec) => ec.content)
      .join('\n\n---\n\n');

    if (!documentContent || documentContent.trim().length < 100) {
      toast.error(
        'Document content is too short. Please upload documents with sufficient content.'
      );
      return;
    }

    if (!ollamaConnected) {
      toast.error('Ollama is not connected. Please ensure Ollama is running.');
      return;
    }

    try {
      setAnalyzingAssets(true);
      toast.loading('Analyzing documents for required assets...', {
        id: 'assets',
      });

      // Combine document content and additional text for analysis
      const combinedContent =
        documentContent +
        (additionalText ? `\n\n---\n\n${additionalText}` : '');

      // Use only document content, not additionalText
      const suggestedAssets = await ollamaService.suggestRequiredAssets(
        combinedContent,
        loadedAssets
      );

      if (suggestedAssets && suggestedAssets.length > 0) {
        // Initialize layout engine with training content
        const layoutEngine = new AssetLayoutEngine(combinedContent, loadedAssets);

        // Create analyzed assets with unique IDs and smart positioning
        const newAnalyzedAssets = suggestedAssets.map((asset, index) => {
          // Calculate smart position using layout engine (position only)
          const { position, metadata } = layoutEngine.placeAsset(asset, {
            workflowOrder: asset.placement_context?.workflow_order || index,
            proximityTo: asset.placement_context?.proximity_to || []
          });

          return {
            ...asset,
            id: `analyzed-${Date.now()}-${index}`,
            assetType: asset.type || 'mesh',
            status: asset.status || 'needed',
            mandatory: asset.mandatory || false,
            // Add intelligent position from layout engine, default rotation/scale
            transform: {
              position,
              rotation: { x: 0, y: 0, z: 0 }, // Default rotation
              scale: { x: 1, y: 1, z: 1 }
            },
            // Store placement metadata for reference
            placementMetadata: metadata
          };
        });

        // Preserve Gemini-placed transforms across re-analysis
        setAnalyzedAssets(prev => {
          const geminiPlaced = prev.filter(a => a.placementSource === 'gemini');
          return newAnalyzedAssets.map(newAsset => {
            const existing = geminiPlaced.find(a => {
              const n1 = a.name.toLowerCase().trim();
              const n2 = newAsset.name.toLowerCase().trim();
              return n1 === n2 || n1.includes(n2) || n2.includes(n1);
            });
            return existing
              ? { ...newAsset, transform: existing.transform, placementSource: 'gemini', placementSurface: existing.placementSurface, placementConfidence: existing.placementConfidence }
              : newAsset;
          });
        });

        // Validate placement for overlaps
        const validation = layoutEngine.validatePlacement(newAnalyzedAssets);
        if (!validation.valid) {
          console.warn('Asset placement issues detected:', validation.issues);
        }

        toast.success(
          `Found ${newAnalyzedAssets.length} assets with intelligent positioning (Pattern: ${layoutEngine.layoutPattern.pattern})`
        );
        // Auto-add mandatory assets that are available
        const mandatoryAvailable = newAnalyzedAssets.filter(
          (asset) => asset.mandatory && asset.status === 'available'
        );
        if (mandatoryAvailable.length > 0) {
          const newRequired = mandatoryAvailable.filter(
            (asset) => !requiredAssets.some((ra) => ra.id === asset.id)
          );
          if (newRequired.length > 0) {
            setRequiredAssets([...requiredAssets, ...newRequired]);
          }
        }

        // Cleanup and dismiss loader
        toast.dismiss('assets');
        setAnalyzingAssets(false);
        return; // Exit early if assets were processed by layout engine
      }

      // Validate that we got assets
      if (!suggestedAssets) {
        console.error('suggestedAssets is null or undefined');
        toast.error('No response from analysis. Please try again.', {
          id: 'assets',
        });
        return;
      }

      if (!Array.isArray(suggestedAssets)) {
        console.error('suggestedAssets is not an array:', suggestedAssets);
        toast.error('Invalid response format from analysis.', {
          id: 'assets',
        });
        return;
      }

      if (suggestedAssets.length === 0) {
        console.warn('No assets found in analysis result');
        toast.error(
          'No assets found in the analysis. Please check the document content.',
          {
            id: 'assets',
          }
        );
        return;
      }



      // Transform suggested assets and check against GridPage assets first
      const analyzedAssetsWithIds = suggestedAssets
        .map((suggested, index) => {


          if (!suggested || !suggested.name) {
            console.warn('Invalid asset structure:', suggested);
            return null;
          }
          // Try to find matching GridPage asset first
          // Use more precise matching - exact name match first, then partial
          const suggestedNameLower = suggested.name.toLowerCase().trim();
          const gridPageMatch = gridPageAssets.find((asset) => {
            const assetNameLower = (asset.name || '').toLowerCase().trim();

            // Exact match (highest priority)
            if (assetNameLower === suggestedNameLower) {
              return true;
            }

            // Check if names are very similar (one contains the other with at least 3 chars)
            if (suggestedNameLower.length >= 3 && assetNameLower.length >= 3) {
              if (
                assetNameLower.includes(suggestedNameLower) ||
                suggestedNameLower.includes(assetNameLower)
              ) {
                // Additional check: ensure it's not too generic (like "button" matching everything)
                const words = suggestedNameLower.split(/\s+/);
                if (words.length > 1 || suggestedNameLower.length > 5) {
                  return true;
                }
              }
            }

            return false;
          });

          if (gridPageMatch) {
            // Validate assetType - only allow valid types
            let assetType =
              suggested.type ||
              suggested.assetType ||
              gridPageMatch.assetType ||
              'mesh';
            if (!VALID_ASSET_TYPES.includes(assetType)) {
              assetType = 'mesh'; // Default to mesh if invalid
            }

            return {
              ...gridPageMatch,
              ...suggested,
              id: gridPageMatch.id,
              type: 'gridpage', // Ensure type is 'gridpage' for GridPage assets
              assetType: assetType, // Validated asset type
              status: 'available',
              description:
                suggested.description || gridPageMatch.description || '',
              usage: suggested.usage || '',
              source: 'gridpage',
              // Preserve LLM-generated transform values if available
              transform: suggested.transform ||
                gridPageMatch.transform || {
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 1, y: 1, z: 1 },
              },
            };
          }

          // Check loaded assets with better matching
          const loadedMatch = loadedAssets.find((asset) => {
            const assetNameLower = (asset.name || '').toLowerCase().trim();
            const suggestedNameLower = suggested.name.toLowerCase().trim();

            // Exact match
            if (assetNameLower === suggestedNameLower) {
              return true;
            }

            // Partial match with minimum length check
            if (suggestedNameLower.length >= 3 && assetNameLower.length >= 3) {
              if (
                assetNameLower.includes(suggestedNameLower) ||
                suggestedNameLower.includes(assetNameLower)
              ) {
                const words = suggestedNameLower.split(/\s+/);
                if (words.length > 1 || suggestedNameLower.length > 5) {
                  return true;
                }
              }
            }

            return false;
          });

          if (loadedMatch) {
            // Validate assetType - only allow valid types
            let assetType =
              suggested.type ||
              suggested.assetType ||
              loadedMatch.assetType ||
              'mesh';
            if (!VALID_ASSET_TYPES.includes(assetType)) {
              assetType = 'mesh'; // Default to mesh if invalid
            }

            return {
              ...loadedMatch,
              ...suggested,
              id: loadedMatch.id,
              type: loadedMatch.type || 'suggested', // Preserve original type or use 'suggested'
              assetType: assetType, // Validated asset type
              status: 'available',
              description:
                suggested.description || loadedMatch.description || '',
              usage: suggested.usage || '',
              source: 'loaded',
              // Preserve LLM-generated transform values if available
              transform: suggested.transform ||
                loadedMatch.transform || {
                position: { x: 0, y: 0, z: 0 },
                rotation: { x: 0, y: 0, z: 0 },
                scale: { x: 1, y: 1, z: 1 },
              },
            };
          }

          // New asset that needs to be provided
          // Map asset type correctly: type should be 'suggested' for analyzed assets, assetType should be the actual type
          // Validate assetType - only allow valid types
          let assetTypeValue = suggested.type || suggested.assetType || 'mesh';
          if (!VALID_ASSET_TYPES.includes(assetTypeValue)) {
            assetTypeValue = 'mesh'; // Default to mesh if invalid
          }

          return {
            id: Math.random().toString(36).substring(2, 15),
            name: suggested.name,
            type: 'suggested', // Type indicates source: 'suggested' means it came from analysis
            assetType: assetTypeValue, // Actual asset type: mesh, image, video, audio
            mandatory: suggested.mandatory || false,
            description: suggested.description || '',
            usage: suggested.usage || '',
            status: 'needed',
            source: 'needed',
            // Store LLM-generated transform values
            transform: suggested.transform || {
              position: { x: 0, y: 0, z: 0 },
              rotation: { x: 0, y: 0, z: 0 },
              scale: { x: 1, y: 1, z: 1 },
            },
          };
        })
        .filter((asset) => asset !== null); // Remove any null entries

      // Update analyzed assets
      setAnalyzedAssets(analyzedAssetsWithIds);

      // Auto-add mandatory assets that are available
      const mandatoryAvailable = analyzedAssetsWithIds.filter(
        (asset) => asset.mandatory && asset.status === 'available'
      );
      if (mandatoryAvailable.length > 0) {
        const newRequired = mandatoryAvailable.filter(
          (asset) => !requiredAssets.some((ra) => ra.id === asset.id)
        );
        if (newRequired.length > 0) {
          setRequiredAssets([...requiredAssets, ...newRequired]);
        }
      }

      toast.success(
        `Analyzed ${analyzedAssetsWithIds.length} asset(s): ${analyzedAssetsWithIds.filter((a) => a.mandatory).length} mandatory, ${analyzedAssetsWithIds.filter((a) => !a.mandatory).length} optional`,
        { id: 'assets' }
      );
    } catch (error) {
      console.error('Asset analysis error:', error);
      toast.error(`Failed to analyze assets: ${error.message}`, {
        id: 'assets',
      });
    } finally {
      setAnalyzingAssets(false);
    }
  };

  // Navigation handlers
  const handleNext = async () => {
    if (currentStep === 1) {
      // Validate that we have content before proceeding
      if (uploadedFiles.length === 0 && !additionalText.trim()) {
        toast.error('Please upload at least one file or add text content');
        return;
      }
      if (processingFiles) {
        toast.error('Please wait for file processing to complete');
        return;
      }
    } else if (currentStep === 4) {
      // Before going to storyboard, generate it if not already generated
      if (chapters.length === 0) {
        const generated = await handleGenerateStoryboard();
        if (!generated) {
          return;
        }
      }
    }

    if (currentStep < 5) {
      // Skip step 3 (Team Setup) - commented out for now
      setCurrentStep(currentStep === 2 ? 4 : currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // Skip step 3 (Team Setup) - commented out for now
      setCurrentStep(currentStep === 4 ? 2 : currentStep - 1);
    }
  };

  // Publish training - creates new VR project first, then publishes
  const handlePublishTraining = async () => {
    // Validate training data (asset-centric validation)
    const validation = validateTrainingData(chapters, requiredAssets);

    if (!validation.valid) {
      // Show detailed error with suggestions
      const errorMessage =
        validation.errors.length > 0
          ? validation.errors.join('\n')
          : 'Validation failed';

      // Check if user wants to proceed anyway (for mandatory assets without moments)
      const hasMissingMandatoryAssets = validation.errors.some(
        (err) =>
          err.includes('Mandatory asset') && err.includes('has no moments')
      );

      if (hasMissingMandatoryAssets) {
        const proceed = window.confirm(
          `${errorMessage}\n\n` +
          `Some mandatory assets don't have moments. This means Unity won't know how to interact with these assets.\n\n` +
          `Options:\n` +
          `1. Go back and edit moments to add touchObjectQuery matching these assets\n` +
          `2. Proceed anyway (not recommended - training may be incomplete)\n\n` +
          `Do you want to proceed anyway?`
        );
        if (!proceed) {
          return;
        }
        // Continue with warnings
      } else {
        toast.error(errorMessage, { duration: 10000 });
        return;
      }
    }

    // Show warnings if any
    if (validation.warnings.length > 0) {
      console.warn('Publishing with warnings:', validation.warnings);
      // Show warnings as info, not blocking
      validation.warnings.forEach((warning) => {
        console.warn(warning);
      });
    }

    try {
      // Show loader on GridPage once we navigate (same loader as GridPage uses)
      store.dispatch(setLoading(true));
      store.dispatch(setLoadingMessage(true));
      toast.loading('Creating VR project and preparing to publish...', {
        id: 'publish',
      });

      // Convert training data to VR format
      const vrData = convertTrainingToVR(chapters, requiredAssets);

      // Generate project name from first chapter or use default
      const projectName =
        chapters.length > 0 && chapters[0].title
          ? `${chapters[0].title} - VR Training`
          : `VR Training - ${new Date().toLocaleDateString()}`;

      // Create new VR project first
      const token = localStorage.getItem('token') || '';
      const createProjectResponse = await axios.post(
        `${process.env.REACT_APP_BASE_URL}experience`,
        {
          project_name: projectName,
          comment: 'Created from Training Module',
          category: 'VR',
          experience_type: 'immersal',
          experience_sdk: 'immersal',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!createProjectResponse.data.success) {
        throw new Error(
          createProjectResponse.data.message || 'Failed to create project'
        );
      }

      const newProjectId = createProjectResponse.data.data._id;
      setProjectId(newProjectId);

      // Add default lights for VR training environment
      // Ambient light for overall scene lighting
      dispatch(
        lightDataUpdate({
          actionType: 'add',
          light: {
            id: `ambient-light-${Date.now()}`,
            type: 'ambient',
            color: '#ffffff',
            intensity: 0.5,
            name: 'Ambient Light',
          },
        })
      );

      // Directional light for main lighting with shadows
      dispatch(
        lightDataUpdate({
          actionType: 'add',
          light: {
            id: `directional-light-${Date.now()}`,
            type: 'directional',
            color: '#ffffff',
            intensity: 1.0,
            position: { x: 5, y: 10, z: 5 },
            rotation: { x: -45, y: 45, z: 0 },
            castShadow: true,
            name: 'Directional Light',
          },
        })
      );

      // POST to create the training record first (new project has no training doc yet;
      // PUT would silently 404 on a non-existent record)
      toast.loading('Saving training data...', { id: 'publish' });
      const trainingPayload = sanitizeForAPI({
        chapters,
        botConfigs,
        loadedAssets: loadedAssets.map(cleanAssetForAPI),
        requiredAssets: requiredAssets.map(cleanAssetForAPI),
        analyzedAssets: analyzedAssets.map(cleanAssetForAPI),
        extractedContents: extractedContents.map((ec) => ({ ...ec })),
        additionalText,
        combinedContent,
        vrData, // include converted VR format so /vr endpoint always has data
      });

      try {
        await axios.post(
          `${process.env.REACT_APP_BASE_URL}experience/training/${newProjectId}`,
          trainingPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
      } catch (postErr) {
        // If POST fails (record may already exist), fall back to PUT
        await axios.put(
          `${process.env.REACT_APP_BASE_URL}experience/training/${newProjectId}`,
          trainingPayload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
      }

      // Apply Gemini positions (only for assets that went through AI placement) to Redux sceneMeshes.
      // Assets without Gemini placement (e.g. the building) keep their current positions.
      const currentMeshes = store.getState().assets_object?.sceneMeshes || [];
      if (currentMeshes.length > 0) {
        const updatedMeshes = currentMeshes.map((mesh) => {
          const geminiAsset = analyzedAssets.find(
            (a) =>
              a.placementSource === 'bedrock' &&
              a.transform?.position &&
              (a.url === mesh.url ||
                a.name === mesh.name ||
                (mesh.file_name && a.name && mesh.file_name.toLowerCase().includes(a.name.toLowerCase())))
          );
          if (geminiAsset) {
            return {
              ...mesh,
              position: {
                x: geminiAsset.transform.position.x,
                y: geminiAsset.transform.position.y,
                z: geminiAsset.transform.position.z,
              },
            };
          }
          return mesh; // keep existing position (building, manually placed items, etc.)
        });
        store.dispatch(setSceneMeshes(updatedMeshes));
      }

      // Save flag to trigger publish when GridPage loads (loader stays until Publish() finishes there)
      localStorage.setItem('autoPublishFromTraining', 'true');
      localStorage.setItem(
        'trainingData',
        JSON.stringify({
          trainingData: vrData,
          published: true,
          publishedAt: new Date().toISOString(),
        })
      );

      toast.success(`Project created! Navigating to scene to publish...`, {
        id: 'publish',
      });

      // Navigate to GridPage - loader (loading + loadingMessage) stays until GridPage's Publish() finishes
      navigate(`/gridpage/${newProjectId}`);
    } catch (error) {
      console.error('Publish training error:', error);
      store.dispatch(setLoading(false));
      store.dispatch(setLoadingMessage(false));
      toast.error(
        `Failed to create project and publish: ${error.response?.data?.message || error.message}`,
        { id: 'publish' }
      );
    }
  };

  return (
    <div className="training-upload-container flex justify-center px-8 bg-black text-white">
      {/* Header with back button */}
      <div className="mb-6 flex gap-6 items-center">
        {/* <button
          onClick={handleBackToGrid}
          className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-white transition-colors border border-gray-700 hover:border-gray-600 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Scene Editor</span>
        </button> */}
        {/* {projectId && (
          <div className="flex items-center gap-4">
            {loadingTrainingData && (
              <span className="text-sm text-gray-400">Loading...</span>
            )}
            {savingTrainingData && (
              <span className="text-sm text-gray-400">Saving...</span>
            )}
            {!loadingTrainingData && !savingTrainingData && chapters.length > 0 && (
              <span className="text-sm text-green-400">Saved</span>
            )}
          </div>
        )} */}
      </div>
      <div className="flex gap-6 w-full h-full min-h-0">
        {/* Sidebar */}
        {currentStep !== 5 && (
          <div className="training-upload-sidebar">
            <StepNavigation
              currentStep={currentStep}
              onStepChange={setCurrentStep}
              onBackToGrid={handleBackToGrid}
            />
          </div>
        )}

        {/* Main Content */}
        <div className="w-full min-w-0 h-full overflow-hidden">
          {currentStep === 1 && (
            <UploadStep
              uploadedFiles={uploadedFiles}
              additionalText={additionalText}
              dragActive={dragActive}
              processingFiles={processingFiles}
              processingError={processingError}
              onFileUpload={handleFiles}
              onFileRemove={removeFile}
              onClearAll={clearAllFiles}
              onAdditionalTextChange={setAdditionalText}
              onDrag={handleDrag}
              onDrop={handleDrop}
              onNext={handleNext}
            />
          )}

          {currentStep === 2 && (
            <ReviewStep
              uploadedFiles={uploadedFiles}
              extractedContents={extractedContents}
              selectedFile={selectedFile}
              onFileSelect={setSelectedFile}
              onContentUpdate={(fileId, newContent) => {
                setExtractedContents((prev) =>
                  prev.map((ec) =>
                    ec.fileId === fileId ? { ...ec, content: newContent } : ec
                  )
                );
              }}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}

          {/* Team Setup step commented out for now - do not remove */}
          {/* {currentStep === 3 && (
            <TeamSetupStep
              botConfigs={botConfigs}
              expandedBots={expandedBots}
              onBotConfigChange={handleBotConfigChange}
              setBotConfigs={setBotConfigs}
              onBotToggle={handleBotToggle}
              onBack={handleBack}
              onNext={handleNext}
            />
          )} */}

          {currentStep === 4 && (
            <AssetSelectionStep
              loadedAssets={loadedAssets}
              requiredAssets={requiredAssets}
              analyzedAssets={analyzedAssets}
              assetViewMode={assetViewMode}
              analyzingAssets={analyzingAssets}
              gridPageAssets={gridPageAssets}
              loadingGridAssets={loadingGridAssets}
              expandedAssetId={expandedAssetId}
              combinedContent={combinedContent}
              autoFetchingAssets={autoFetchingAssets}
              fetchProgress={fetchProgress}
              fetchingAssetIds={fetchingAssetIds}
              onAssetRemove={handleAssetRemove}
              onRequiredAssetAdd={handleRequiredAssetAdd}
              onViewModeChange={setAssetViewMode}
              onReanalyze={handleReanalyzeAssets}
              onAutoFetchAssets={handleAutoFetchAssets}
              onAddGridPageAsset={handleAddGridPageAsset}
              onAssetInfoUpdate={handleAssetInfoUpdate}
              onAddAnalyzedAsset={handleOpenAssetPicker}
              onUploadAssetForAnalyzed={handleUploadAssetForAnalyzed}
              onExpandedAssetChange={setExpandedAssetId}
              assetPicker={assetPicker}
                            onSelectPreview={handleSelectPreview}
              onRetryPreview={handleRetryPreview}
              onConfirmSelectedPreview={handleConfirmSelectedPreview}
              onAiAutoFetch={handleAiAutoFetch}
              onCloseAssetPicker={() => { assetPicker.previews.forEach(p => { if (p.blobUrl) URL.revokeObjectURL(p.blobUrl); }); setAssetPicker({ open: false, asset: null, candidates: [], searching: false, previews: [], selectedPreviewIdx: null, downloadingAll: false }); }}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}

          {currentStep === 5 && (
            <StoryboardStep
              chapters={chapters}
              searchQuery={searchQuery}
              selectedMoment={selectedMoment}
              activeTab={activeTab}
              isTechnicalMode={isTechnicalMode}
              generatingStoryboard={generatingStoryboard}
              projectId={projectId}
              onSearchChange={setSearchQuery}
              onMomentSelect={setSelectedMoment}
              onTabChange={setActiveTab}
              onModeChange={setIsTechnicalMode}
              onMomentUpdate={handleMomentUpdate}
              onSaveInteraction={() => saveTrainingData(projectId)}
              onGenerateStoryboard={handleGenerateStoryboard}
              onPublish={handlePublishTraining}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}
        </div>
      </div>
    </div>
  );
}
